import React, { useEffect, useState, useMemo } from 'react';
import MobileLayout from '../../../components/layout/MobileLayout';
import TransactionList from '../../../components/ui/TransactionList';
import { getOrders, getMerchantPaymentSettings, getUserInfo } from '../../../services/useAPICaller';
import { Order, PaymentSettings } from '../../../types/payment.types';
import { useI18n } from '../../../i18n/I18n';
import ProfileSettingsMenu from '../../../components/ui/ProfileSettingsMenu';

export default function HomeView() {
    const { t } = useI18n();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
    const [userProfile, setUserProfile] = useState<{ pictureUrl?: string; name?: string } | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState<number>(5);

    const fetchHomeData = async () => {
        try {
            setLoading(true);
            // Verify if we have a token before trying to fetch
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                setLoading(false);
                return;
            }

            const ordersData = await getOrders(0, 50); // Fetch first 50 orders
            if (ordersData && ordersData.content) {
                setOrders(ordersData.content);
            }

            getMerchantPaymentSettings((data) => {
                setPaymentSettings(data);
            });

            try {
                const userInfo = await getUserInfo();
                if (userInfo) {
                    setUserProfile(userInfo);
                }
            } catch (err) {
                console.error("Error fetching user info:", err);
            }

        } catch (error) {
            console.error("Error fetching home data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHomeData();
    }, []);

    // handleLoginSuccess was removed since GoogleLogin is no longer used here

    const handleLogout = () => {
        localStorage.removeItem('jwtToken');
        setPaymentSettings(null);
        setOrders([]);
        setUserProfile(null);
        setIsProfileMenuOpen(false);
        window.location.reload(); // Reload to clear any other state/cache
    };

    const filteredOrders = useMemo(() => {
        if (!searchTerm.trim()) return orders;
        const searchLower = searchTerm.toLowerCase();
        return orders.filter(order =>
            (order.concept && order.concept.toLowerCase().includes(searchLower)) ||
            (order.orderId && order.orderId.toLowerCase().includes(searchLower)) ||
            (order.publicOrderId && order.publicOrderId.toLowerCase().includes(searchLower)) ||
            (order.token && order.token.toLowerCase().includes(searchLower)) ||
            (order.chain && order.chain.toLowerCase().includes(searchLower)) ||
            (order.sessionType && order.sessionType.toLowerCase().includes(searchLower))
        );
    }, [orders, searchTerm]);

    const visibleOrders = useMemo(() => {
        return filteredOrders.slice(0, visibleCount);
    }, [filteredOrders, visibleCount]);

    const groupedOrders = useMemo(() => {
        const groups: { date: string; orders: Order[] }[] = [];
        visibleOrders.forEach(order => {
            if (!order.createdDate) return;
            const date = new Date(
                parseInt(order.createdDate[0]),
                parseInt(order.createdDate[1]) - 1,
                parseInt(order.createdDate[2])
            );
            const today = new Date();
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);

            let key = date.toLocaleDateString();
            if (date.toDateString() === today.toDateString()) {
                key = t('home.today');
            } else if (date.toDateString() === yesterday.toDateString()) {
                key = t('home.yesterday');
            } else {
                key = date.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
            }

            const existingGroup = groups.find(g => g.date === key);
            if (existingGroup) {
                existingGroup.orders.push(order);
            } else {
                groups.push({ date: key, orders: [order] });
            }
        });
        return groups;
    }, [visibleOrders, t]);

    const handleShowMore = () => {
        setVisibleCount(prev => prev + 10);
    };


    // Helper to extract wallet address from payment settings logic (if available)
    const displayedWallet = paymentSettings?.chains?.[0]?.publicAddress
        ? `${paymentSettings.chains[0].publicAddress.substring(0, 6)}...${paymentSettings.chains[0].publicAddress.substring(paymentSettings.chains[0].publicAddress.length - 4)}`
        : 'Please sign in';

    // Determine profile image source: Google Picture -> Merchant Logo -> Default Icon
    const profileImageSrc = userProfile?.pictureUrl || paymentSettings?.merchantLogoUrl;

    return (
        <MobileLayout>
            {/* Header */}
            <div className="px-6 lg:px-10 pt-14 lg:pt-10 pb-2 flex justify-between items-center z-30 w-full max-w-md lg:max-w-5xl relative gap-4">
                <div className="flex-1 min-w-0">
                    <h1 className="text-3xl font-bold text-txt-heading tracking-tight leading-none mb-1 truncate">
                        {paymentSettings?.merchantName || 'Merchant'}
                    </h1>
                    <p className="text-sm text-slate-grey font-medium">
                        {displayedWallet || t('home.signInToView')}
                    </p>
                </div>
                <div className="relative lg:hidden">
                    <button
                        onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                        className="w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-transform active:scale-95 overflow-hidden"
                        style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-border)' }}
                    >
                        {profileImageSrc ? (
                            <img src={profileImageSrc} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                            <span className="material-symbols-outlined text-txt-heading text-[24px]">person</span>
                        )}
                    </button>

                    {/* Profile Settings Menu */}
                    <ProfileSettingsMenu
                        isOpen={isProfileMenuOpen}
                        onClose={() => setIsProfileMenuOpen(false)}
                        paymentSettings={paymentSettings}
                        userProfile={userProfile}
                        onLogout={handleLogout}
                    />
                </div>
            </div>

            {/* Search Bar */}
            <div className="px-4 lg:px-10 py-4 z-10 w-full max-w-md lg:max-w-5xl">
                <div className="rounded-xl px-4 py-2.5 flex items-center gap-3 transition-all focus-within:border-electric-blue/40" style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)' }}>
                    <span className="material-symbols-outlined text-slate-grey text-[20px]">search</span>
                    <input
                        className="bg-transparent border-none text-txt-heading placeholder-slate-grey focus:ring-0 w-full text-sm font-medium p-0 focus:outline-none"
                        placeholder={t('home.searchPlaceholder')}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Transaction List */}
            <div className="flex-1 w-full max-w-md lg:max-w-5xl overflow-y-auto no-scrollbar px-0 pb-32 lg:pb-10 z-0">
                <div className="space-y-0">
                    <TransactionList
                        loading={loading}
                        hasSettings={!!paymentSettings}
                        groupedOrders={groupedOrders}
                        filteredOrders={filteredOrders}
                        visibleCount={visibleCount}
                        onShowMore={handleShowMore}
                    />
                </div>

                <div className="h-24"></div>
            </div>
        </MobileLayout>
    );
}
